import { Injectable, inject, signal } from '@angular/core';
import Category from '../models/Category.model';
import Menu from '../models/Menu.model';
import OptionGroup from '../models/OptionGroup.model';
import Product from '../models/Product.model';
import { AppSettingsService } from './app-settings.service';
import { PBService } from './pocketbase.service';

@Injectable({ providedIn: 'root' })
export class MenuService {
  private PB = inject(PBService);
  private settingsService = inject(AppSettingsService);

  // --- Current Route State ---
  private currentCustomerID: string | undefined;
  private currentMenuID: string | undefined;
  private currentCategoryID: string | undefined;
  private currentProductID: string | undefined;

  // --- Menu State ---
  public menuData = signal<Menu[]>([]);
  public totalMenus = signal(0);
  public currentMenuPage = signal(1);
  public menuItemsPerPage = signal(25);
  public menuFilterText = signal('');
  public menusLoaded = signal(false);
  private menuFilterTimeout: any;

  // --- Category State ---
  public categoryData = signal<Category[]>([]);
  public totalCategories = signal(0);
  public currentCategoryPage = signal(1);
  public categoryItemsPerPage = signal(25);
  public categoryFilterText = signal('');
  public categoriesLoaded = signal(false);
  private categoryFilterTimeout: any;

  // --- Category Map Cache for Hierarchical Navigation ---
  public categoryMap = signal<Map<string, Category>>(new Map());
  private loadedMenuCategoriesId: string | undefined;

  public getCategoryById(id: string): Category | undefined {
    return this.categoryMap().get(id);
  }

  public async ensureCategory(id: string): Promise<Category | null> {
    const existing = this.categoryMap().get(id);
    if (existing) return existing;
    try {
      const cat = await this.PB.getCategory(id);
      if (cat) {
        this.updateCategoryMap([cat]);
      }
      return cat;
    } catch {
      return null;
    }
  }

  public updateCategoryMap(categories: Category[]) {
    const current = new Map(this.categoryMap());
    let changed = false;
    for (const cat of categories) {
      if (cat && cat.id) {
        current.set(cat.id, cat);
        changed = true;
      }
    }
    if (changed) {
      this.categoryMap.set(current);
    }
  }

  public async preloadMenuCategories(customerID: string, menuID: string): Promise<void> {
    if (this.loadedMenuCategoriesId === menuID) return;
    try {
      const allCategories = await this.PB.getAllCategories(customerID, menuID);
      this.updateCategoryMap(allCategories);
      this.loadedMenuCategoriesId = menuID;
    } catch (e) {
      console.error('Failed to preload menu categories:', e);
    }
  }

  // --- Sub-Category State (New) ---
  public subCategories = signal<Category[]>([]);
  public subCategoriesLoaded = signal(false);

  // --- Product State ---
  public productData = signal<Product[]>([]);
  public totalProducts = signal(0);
  public currentProductPage = signal(1);
  public productItemsPerPage = signal(25);
  public productFilterText = signal('');
  public productsLoaded = signal(false);
  private productFilterTimeout: any;

  // --- Single Product State ---
  public singleProduct = signal<Product | undefined>(undefined);
  public singleProductLoading = signal<boolean>(true);

  // --- OptionGroup State ---
  public optionGroups = signal<OptionGroup[]>([]);
  public optionGroupsLoaded = signal(false);

  async loadSingleProduct(productID: string): Promise<void> {
    if (this.currentProductID === productID) {
      this.singleProductLoading.set(false);
      return;
    }

    this.currentProductID = productID;
    this.singleProductLoading.set(true);

    try {
      const prod = await this.PB.getProduct(productID);
      this.singleProduct.set(prod);
    } catch (error) {
      console.error('Error loading single product:', error);
      this.singleProduct.set(undefined);
    } finally {
      this.singleProductLoading.set(false);
    }
  }

  // --- Menu Methods ---
  async loadMenus(customerID: string): Promise<void> {
    if (this.currentCustomerID !== customerID) {
      this.currentCustomerID = customerID;
      this.resetMenus();
      this.resetCategories();
      this.resetProducts();
      this.resetOptionGroups();
      this.resetSingleProduct();
      this.currentMenuID = undefined;
      this.currentCategoryID = undefined;
    }

    if (this.menusLoaded()) return;

    this.menusLoaded.set(false);
    try {
      const paginatedResult = await this.PB.getMenus({
        customerID: this.currentCustomerID!,
        page: this.currentMenuPage(),
        perPage: this.menuItemsPerPage(),
        filter: this.menuFilterText() || undefined,
      });
      this.menuData.set(paginatedResult.items);
      this.totalMenus.set(paginatedResult.totalItems);
      this.currentMenuPage.set(paginatedResult.page);
      this.menuItemsPerPage.set(paginatedResult.perPage);
      this.menusLoaded.set(true);
      this.syncActiveMenuCurrency();
    } catch (error) {
      console.error('Error loading menus:', error);
      this.resetMenus();
    }
  }

  private syncActiveMenuCurrency() {
    if (!this.currentMenuID) return;
    const activeMenu = this.menuData().find(m => m.id === this.currentMenuID);
    if (activeMenu?.Currency) {
      this.settingsService.selectedCurrency.set(activeMenu.Currency);
    }
  }

  public async onMenuPageChange(page: number, perPage: number) {
    this.currentMenuPage.set(page);
    this.menuItemsPerPage.set(perPage);
    this.menusLoaded.set(false);
    await this.loadMenus(this.currentCustomerID!);
  }

  public onMenuFilterChange(filter: string) {
    this.menuFilterText.set(filter);
    this.currentMenuPage.set(1);
    this.menusLoaded.set(false);

    if (this.menuFilterTimeout) clearTimeout(this.menuFilterTimeout);
    this.menuFilterTimeout = setTimeout(() => {
      this.loadMenus(this.currentCustomerID!);
    }, 500);
  }

  public getCurrentCustomerID(): string | undefined {
    return this.currentCustomerID;
  }

  public getCurrentMenuID(): string | undefined {
    return this.currentMenuID;
  }

  public getCurrentCategoryID(): string | undefined {
    return this.currentCategoryID;
  }

  public setCategory(categoryID: string | undefined): void {
    if (this.currentCategoryID !== categoryID) {
      this.currentCategoryID = categoryID;
      this.productData.set([]);
      this.totalProducts.set(0);
      this.currentProductPage.set(1);
      this.productFilterText.set('');
      this.productsLoaded.set(false);
      this.resetSingleProduct();
      this.subCategories.set([]);
      this.subCategoriesLoaded.set(false);
    }
  }

  // --- Category Methods ---
  async loadCategories(
    customerID: string,
    menuID: string,
    parentCategoryID: string | null = null
  ): Promise<void> {
    await this.loadMenus(customerID);

    this.currentCustomerID = customerID;

    if (this.currentMenuID !== menuID) {
      this.resetCategories();
      this.resetProducts();
      this.resetSingleProduct();
      this.currentMenuID = menuID;
      this.syncActiveMenuCurrency();
    }

    if (parentCategoryID === null) {
      this.resetProducts();
      this.currentCategoryID = undefined;
    }

    if (this.categoriesLoaded() && this.currentMenuID === menuID) return;

    this.categoriesLoaded.set(false);
    try {
      const paginatedResult = await this.PB.getCategories({
        customerID: this.currentCustomerID || customerID,
        menuID: this.currentMenuID || menuID,
        parentCategoryID: parentCategoryID,
        page: this.currentCategoryPage(),
        perPage: this.categoryItemsPerPage(),
        filter: this.categoryFilterText() || undefined,
      });

      if (menuID !== this.currentMenuID) return;

      this.categoryData.set(paginatedResult.items);
      this.updateCategoryMap(paginatedResult.items);
      this.totalCategories.set(paginatedResult.totalItems);
      this.currentCategoryPage.set(paginatedResult.page);
      this.categoryItemsPerPage.set(paginatedResult.perPage);
      this.categoriesLoaded.set(true);

      // Preload the full category tree for instant synchronous navigation
      this.preloadMenuCategories(this.currentCustomerID || customerID, menuID);
    } catch (error) {
      if (menuID !== this.currentMenuID) return;
      console.error('Error loading categories:', error);
      this.resetCategories();
    }
  }

  async loadSubCategories(customerID: string, menuID: string, parentCategoryID: string) {
    this.currentCustomerID = customerID;
    this.currentMenuID = menuID;

    if (this.currentCategoryID !== parentCategoryID) {
      this.setCategory(parentCategoryID);
    }
    this.subCategories.set([]);
    this.subCategoriesLoaded.set(false);

    try {
      const result = await this.PB.getCategories({
        customerID: this.currentCustomerID || customerID,
        menuID: this.currentMenuID || menuID,
        parentCategoryID: parentCategoryID,
        page: 1,
        perPage: 999,
      });

      if (parentCategoryID !== this.currentCategoryID) return;

      this.subCategories.set(result.items);
      this.updateCategoryMap(result.items);
      this.subCategoriesLoaded.set(true);

      // Preload full category tree so parent hierarchy is indexed
      this.preloadMenuCategories(this.currentCustomerID || customerID, menuID);
    } catch (error) {
      if (parentCategoryID !== this.currentCategoryID) return;
      console.error('Error loading sub-categories:', error);
      this.subCategories.set([]);
      this.subCategoriesLoaded.set(true); // Set true so UI knows we tried
    }
  }

  public async onCategoryPageChange(page: number, perPage: number) {
    this.currentCategoryPage.set(page);
    this.categoryItemsPerPage.set(perPage);
    this.categoriesLoaded.set(false);
    // Reload root categories
    await this.loadCategories(this.currentCustomerID!, this.currentMenuID!, null);
  }

  public onCategoryFilterChange(filterValue: string) {
    this.categoryFilterText.set(filterValue);
    this.currentCategoryPage.set(1);
    this.categoriesLoaded.set(false);

    if (this.categoryFilterTimeout) clearTimeout(this.categoryFilterTimeout);
    this.categoryFilterTimeout = setTimeout(() => {
      this.loadCategories(this.currentCustomerID!, this.currentMenuID!, null);
    }, 500);
  }

  // --- Product Methods ---
  async loadProducts(customerID: string, menuID: string, categoryID: string): Promise<void> {
    this.currentCustomerID = customerID;
    this.currentMenuID = menuID;
    this.setCategory(categoryID);

    // Preload menu categories for parent navigation
    this.preloadMenuCategories(customerID, menuID);

    const optionGroupsPromise = this.loadOptionGroups(customerID, menuID);

    if (this.productsLoaded() && this.currentCategoryID === categoryID) return;

    this.productsLoaded.set(false);
    try {
      const paginatedResult = await this.PB.getProducts({
        customerID: this.currentCustomerID || customerID,
        menuID: this.currentMenuID || menuID,
        categoryID: categoryID,
        page: this.currentProductPage(),
        perPage: this.productItemsPerPage(),
        filter: this.productFilterText() || undefined,
      });

      if (categoryID !== this.currentCategoryID) return;

      this.productData.set(paginatedResult.items);
      this.totalProducts.set(paginatedResult.totalItems);
      this.currentProductPage.set(paginatedResult.page);
      this.productItemsPerPage.set(paginatedResult.perPage);
      this.productsLoaded.set(true);
    } catch (error) {
      if (categoryID !== this.currentCategoryID) return;
      console.error('Error loading products:', error);
      this.resetProducts();
      this.resetSingleProduct();
    } finally {
      await optionGroupsPromise;
    }
  }

  public async onProductPageChange(page: number, perPage: number) {
    this.currentProductPage.set(page);
    this.productItemsPerPage.set(perPage);
    this.productsLoaded.set(false);
    await this.loadProducts(this.currentCustomerID!, this.currentMenuID!, this.currentCategoryID!);
  }

  public onProductFilterChange(filter: string) {
    this.productFilterText.set(filter);
    this.currentProductPage.set(1);
    this.productsLoaded.set(false);

    if (this.productFilterTimeout) clearTimeout(this.productFilterTimeout);
    this.productFilterTimeout = setTimeout(() => {
      this.loadProducts(this.currentCustomerID!, this.currentMenuID!, this.currentCategoryID!);
    }, 500);
  }

  // --- OptionGroup Methods ---
  async loadOptionGroups(customerID: string, menuID: string): Promise<void> {
    if (this.optionGroupsLoaded()) return;
    if (!customerID || !menuID) return;

    this.optionGroupsLoaded.set(false);
    try {
      const paginatedResult = await this.PB.getOptionGroups({
        customerID: customerID,
        menuID: menuID,
        page: 1,
        perPage: 9999,
      });
      this.optionGroups.set(paginatedResult.items);
      this.optionGroupsLoaded.set(true);
    } catch (error) {
      console.error('Error loading option groups:', error);
      this.resetOptionGroups();
    }
  }

  // --- Reset Methods ---
  public resetMenus() {
    this.menuData.set([]);
    this.totalMenus.set(0);
    this.currentMenuPage.set(1);
    this.menuFilterText.set('');
    this.menusLoaded.set(false);
  }

  public resetCategories() {
    this.categoryData.set([]);
    this.totalCategories.set(0);
    this.currentCategoryPage.set(1);
    this.categoryFilterText.set('');
    this.categoriesLoaded.set(false);
    this.subCategories.set([]);
    this.subCategoriesLoaded.set(false);
    this.categoryMap.set(new Map());
    this.loadedMenuCategoriesId = undefined;
  }

  public resetProducts() {
    this.productData.set([]);
    this.totalProducts.set(0);
    this.currentProductPage.set(1);
    this.productFilterText.set('');
    this.productsLoaded.set(false);
  }

  public resetSingleProduct() {
    this.singleProduct.set(undefined);
    this.singleProductLoading.set(true);
    this.currentProductID = undefined;
  }

  public resetOptionGroups() {
    this.optionGroups.set([]);
    this.optionGroupsLoaded.set(false);
  }
}
